import { IconQrcode } from '@tabler/icons-react'
import styled from 'styled-components'

import { WorkerQrCodeSignatureCaptureBlock as RenderWorkerQrCodeSignatureCaptureBlock } from '../../templateRenderer/CaptureBlocks/WorkerQrCodeSignatureCaptureBlock'
import { WorkerQrCodeSignatureDisplayBlock as RenderWorkerQrCodeSignatureDisplayBlock } from '../../templateRenderer/DisplayBlocks/WorkerQrCodeSignatureDisplayBlock'
import {
  CompetentPersonSignatureItem,
  WorkerQrCodeSignatureCaptureBlock,
  WorkerQrCodeSignatureDisplayBlock,
  WorkerQrCodeSignatureItem,
} from '../../types/blueprint'

import { BaseBlockConfig, Field, TypeOption } from './_BaseBlockConfig'

import { Text } from '@/components/shared-components/BUI/Text'
import { pluralise } from '@/utils/pluralise'

import { BuildPassModule, TemplateType } from 'types/api.graphql'

export const WorkersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export type WorkerQrCodeSignatureCaptureBlockValues = {
  workers: WorkerQrCodeSignatureItem[]
  competentPersonSignature: CompetentPersonSignatureItem
}

class Config extends BaseBlockConfig<WorkerQrCodeSignatureCaptureBlock> {
  public override kind = 'capture' as const
  public override type = 'workerQrCodeSignature' as const

  public override enableBlockToColumnDisplay = true

  public override blockTypeConfig: TypeOption = {
    kind: this.kind,
    value: this.type,
    label: 'Sign-in sheet',
    icon: IconQrcode,
    exclusiveTemplateTypes: [TemplateType.Inspection],
    builderModule: BuildPassModule.WorkerQrCodes,
    helpText:
      "Scan worker's induction QR codes from their app or physical stickers to mark acknowledgement of the form.",
  }

  public override blockFieldsConfig: Field[] = []

  public override mapFormValueToBlock(
    block: WorkerQrCodeSignatureCaptureBlock,
    value: WorkerQrCodeSignatureCaptureBlockValues
  ): WorkerQrCodeSignatureCaptureBlock {
    return {
      ...block,
      workers: value?.workers?.map((worker) => {
        return {
          workerId: worker.workerId,
          firstName: worker.firstName,
          lastName: worker.lastName,
          createdAt: worker.createdAt,
          key: worker.key,
          signedViaQrCode: worker.signedViaQrCode,
        }
      }),
      competentPersonSignature: value?.competentPersonSignature,
    }
  }

  public override renderBlock(
    block: WorkerQrCodeSignatureCaptureBlock
  ): React.ReactNode {
    return (
      <RenderWorkerQrCodeSignatureCaptureBlock
        block={block}
        rules={
          block.required
            ? [
                {
                  required: true,
                  validator: async (_, value) => {
                    if (!value?.workers?.length) {
                      return Promise.reject(
                        'Please include at least one worker in attendance'
                      )
                    }
                    if (!value?.competentPersonSignature?.signee?.length) {
                      return Promise.reject(
                        "Please enter the competent person's name"
                      )
                    }
                    if (!value?.competentPersonSignature?.url) {
                      return Promise.reject('Click sign to add your signature')
                    }

                    return Promise.resolve()
                  },
                },
              ]
            : []
        }
      />
    )
  }

  public override renderDisplayBlock(
    block: WorkerQrCodeSignatureDisplayBlock
  ): React.ReactNode {
    return <RenderWorkerQrCodeSignatureDisplayBlock block={block} />
  }

  public override mapBlockToColumnDisplay(
    block: WorkerQrCodeSignatureCaptureBlock
  ): React.ReactNode {
    if (
      (!block?.workers || block?.workers?.length === 0) &&
      !block?.competentPersonSignature
    ) {
      return (
        <WorkersWrapper>
          <Text>No worker signatures</Text>
        </WorkersWrapper>
      )
    }

    return (
      <WorkersWrapper>
        <Text>
          {pluralise(
            (block?.workers?.length ?? 0) +
              (block?.competentPersonSignature?.url ? 1 : 0),
            'signature'
          )}
        </Text>
      </WorkersWrapper>
    )
  }
}

export const WorkerQrCodeSignatureCaptureBlockConfig = new Config()
